import {
    ANALYTICS_CHANNEL, DV360_CHANNEL, ESCALAFY_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, KLAVIYO_CHANNEL,
    LINKEDIN_CHANNEL, MERCADO_ADS_CHANNEL, SEARCH_CONSOLE_CHANNEL, SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL,
    TIKTOK_CHANNEL, VTEX_CHANNEL, WOOCOMMERCE_CHANNEL,
} from '../../channels/channels';
import {canReconnectCredential} from '../connections/reconnectCredential';

/**
 * Conexiones › Plataformas en la 2.1, sin React ni red (tanda «Agencia 2.1», contrato §2).
 *
 * Todo lo que la pantalla DECIDE vive acá: a qué familia va cada canal, en qué estado está, qué
 * accesos necesitan atención, qué se ofrece en «Dashbo también se conecta con…» y qué ocultó el
 * usuario. La pantalla sólo dibuja lo que esto devuelve, y los tests miran esto.
 */

// Mercado Ads entra sólo donde el backend lo habilita (`channels_allowed`, por agencia): estar en esta
// lista no lo muestra a nadie más.
export const PAID_MEDIA_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL,
    MERCADO_ADS_CHANNEL];

/** Las familias, en el orden de la pantalla (D6). */
export const FAMILIES = [
    {key: 'paid_media', channels: PAID_MEDIA_CHANNELS},
    {key: 'analytics', channels: [ANALYTICS_CHANNEL, SEARCH_CONSOLE_CHANNEL]},
    {key: 'email', channels: [KLAVIYO_CHANNEL]},
    {key: 'stores', channels: [SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, WOOCOMMERCE_CHANNEL, VTEX_CHANNEL]},
    {key: 'profitability', channels: [ESCALAFY_CHANNEL]},
];

/**
 * El orden del bloque de descubrimiento: primero lo que más suma a lo que una agencia ya tiene (SEO,
 * email, rentabilidad, las tiendas), y la pauta al final, que es lo que casi todas ya conectaron.
 */
export const DISCOVERY_ORDER = [
    SEARCH_CONSOLE_CHANNEL, KLAVIYO_CHANNEL, ESCALAFY_CHANNEL, WOOCOMMERCE_CHANNEL, VTEX_CHANNEL,
    SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, ANALYTICS_CHANNEL, ...PAID_MEDIA_CHANNELS,
];

/** Los que llevan la pastilla «Nuevo» en su tarjeta. Escalafy entró el 01/10/2026 y Mercado Ads el 20/10. */
export const NEW_CHANNELS = [ESCALAFY_CHANNEL, MERCADO_ADS_CHANNEL];

// Dónde viaja cada canal en `GET agency/connections/`: los accesos (credenciales de la agencia) y las
// tiendas, que el backend manda como cuentas sueltas.
const CREDENTIALS_KEY_BY_CHANNEL = {
    [GOOGLE_CHANNEL]: 'google_credentials',
    [FACEBOOK_CHANNEL]: 'facebook_credentials',
    [TIKTOK_CHANNEL]: 'tiktok_credentials',
    [LINKEDIN_CHANNEL]: 'linkedin_credentials',
    [DV360_CHANNEL]: 'dv360_credentials',
    [MERCADO_ADS_CHANNEL]: 'mercado_ads_credentials',
    [ANALYTICS_CHANNEL]: 'ga4_credentials',
    [SEARCH_CONSOLE_CHANNEL]: 'gsc_credentials',
    [ESCALAFY_CHANNEL]: 'escalafy_credentials',
};

const STORES_KEY_BY_CHANNEL = {
    [SHOPIFY_CHANNEL]: 'shopify_ad_accounts_data',
    [TIENDA_NUBE_CHANNEL]: 'tienda_nube_ad_accounts_data',
    [KLAVIYO_CHANNEL]: 'klaviyo_ad_accounts_data',
    [WOOCOMMERCE_CHANNEL]: 'woocommerce_ad_accounts_data',
    [VTEX_CHANNEL]: 'vtex_ad_accounts_data',
};

export const STATUS_DISCONNECTED = 'DISCONNECTED';

// LinkedIn corta el acceso a los 60 días (card fqyBbFvp): desde una semana antes se avisa y se ofrece
// Reconectar, igual que el mail que manda el backend. Mismo número que `CredentialRow`.
export const EXPIRY_WARNING_MS = 7 * 24 * 60 * 60 * 1000;

export function isStoreChannel(channel) {
    return Object.prototype.hasOwnProperty.call(STORES_KEY_BY_CHANNEL, channel);
}

/** ¿El canal llega por accesos de la agencia (`*_credentials`)? Las tiendas y las planillas, no. */
export function hasAgencyCredentials(channel) {
    return Object.prototype.hasOwnProperty.call(CREDENTIALS_KEY_BY_CHANNEL, channel);
}

/** La respuesta de `GET agency/connections/` (su `data`), con cada lista garantizada. */
export function connectionsFromResponse(data) {
    const source = data || {};
    const credentials = {};
    Object.keys(CREDENTIALS_KEY_BY_CHANNEL).forEach((channel) => {
        const list = source[CREDENTIALS_KEY_BY_CHANNEL[channel]];
        credentials[channel] = Array.isArray(list) ? list.filter(Boolean) : [];
    });
    const stores = {};
    Object.keys(STORES_KEY_BY_CHANNEL).forEach((channel) => {
        const list = source[STORES_KEY_BY_CHANNEL[channel]];
        // Sólo las que tienen un acceso de la AGENCIA: es el mismo filtro de la tabla clásica
        // (`EcommerceConnectionsComponent`). Las otras son cuentas que conectó otro camino.
        stores[channel] = Array.isArray(list)
            ? list.filter((store) => store && store.has_agency_ad_account_credential === true)
            : [];
    });
    return {
        channelsAllowed: Array.isArray(source.channels_allowed) ? source.channels_allowed : [],
        useGa4Scripts: source.use_ga4_thou_scripts === true,
        credentials,
        stores,
    };
}

/** ¿Hay algún acceso sincronizando? Mientras haya uno, la pantalla vuelve a pedir (poll). */
export function anyCredentialSyncing(connections) {
    if (!connections) return false;
    return Object.values(connections.credentials)
        .some((list) => list.some((credential) => credential.is_syncing === true));
}

/**
 * El vencimiento de un acceso: `'expired'`, `'expiring'` (falta una semana o menos), `'auto_renews'`
 * o `null`. Es la lógica de `CredentialRow.expiryState`: sólo LinkedIn vence hoy, y una conexión
 * creada desde el portal la gestiona el cliente.
 */
export function expiryStateOf(channel, credential, now = Date.now()) {
    if (channel !== LINKEDIN_CHANNEL || !credential) return null;
    if (credential.managed_by_client) return null;
    if (credential.auto_renews) {
        return credential.status === STATUS_DISCONNECTED ? null : 'auto_renews';
    }
    if (!credential.expires_at) return null;
    const msLeft = new Date(credential.expires_at).getTime() - now;
    if (Number.isNaN(msLeft)) return null;
    if (msLeft <= 0) return 'expired';
    if (msLeft <= EXPIRY_WARNING_MS) return 'expiring';
    return null;
}

/**
 * ¿La salida de un acceso caído es mandarle al vendedor un link nuevo? Es una conexión de Mercado Ads
 * que hizo el cliente (desde su portal o con el link): la agencia no la reconecta con su OAuth, pero
 * tiene qué hacer, así que se le pide igual (en este canal el link es el camino recomendado).
 */
export function fixedBySendingLink(channel, credential) {
    return channel === MERCADO_ADS_CHANNEL && !!credential && !!credential.managed_by_client;
}

/**
 * Por qué un acceso necesita atención: `'expired'`, `'disconnected'`, `'expiring'` o `null`. Es el
 * `needsReconnect` de `CredentialRow` con el motivo nombrado: si la agencia no lo puede reconectar ni
 * mandar un link nuevo, tampoco se lo pide.
 */
export function attentionKindOf(channel, credential, now = Date.now()) {
    if (!credential) return null;
    if (!canReconnectCredential(channel, credential) && !fixedBySendingLink(channel, credential)) return null;
    const expiry = expiryStateOf(channel, credential, now);
    // Vencido antes que desconectado: un LinkedIn vencido suele estar también desconectado, y «venció
    // el 28 mar» dice más que «se desconectó».
    if (expiry === 'expired') return 'expired';
    if (credential.status === STATUS_DISCONNECTED) return 'disconnected';
    if (expiry === 'expiring') return 'expiring';
    return null;
}

function attentionDateOf(kind, credential) {
    if (kind === 'disconnected') return credential.disconnected_since || null;
    return credential.expires_at || null;
}

const ATTENTION_PRIORITY = {expired: 0, disconnected: 1, expiring: 2};

/**
 * El estado de una plataforma, de lo más grave a lo menos: accesos que ya no traen datos (`allExpired`
 * dice si TODOS vencieron: «vencido» sería falso para uno que se desconectó), accesos por vencer,
 * tiendas que perdieron el acceso (el clásico lo muestra siempre en su columna «Accesible»; acá no
 * puede quedar escondido detrás de una fila verde y plegada), tiendas sin cliente, conectada.
 */
function statusOf(platform) {
    if (platform.legacyGa4) return {kind: 'legacy'};
    const failing = platform.attention.filter((item) => item.kind !== 'expiring');
    if (failing.length > 0) {
        return {kind: 'attention', count: failing.length, allExpired: failing.every((item) => item.kind === 'expired')};
    }
    if (platform.attention.length > 0) return {kind: 'expiring', count: platform.attention.length};
    if (platform.storesWithoutAccess > 0) return {kind: 'store_no_access', count: platform.storesWithoutAccess};
    if (platform.unassignedStores > 0) return {kind: 'store_unassigned', count: platform.unassignedStores};
    return {kind: 'connected'};
}

/**
 * Una entrada por canal habilitado (`channels_allowed`), en el orden de las familias. Cada una dice
 * si está conectada, cuántas cuentas trae, qué accesos piden atención y su estado.
 *
 * GA4 por scripts (`use_ga4_thou_scripts`, la integración vieja por planilla) entra aunque GA4 no
 * esté habilitado, igual que en el clásico, y monta el `AnalyticsComponent` de siempre.
 */
export function platformsOf(connections, now = Date.now()) {
    if (!connections) return [];
    const allowed = new Set(connections.channelsAllowed);
    const result = [];
    FAMILIES.forEach((family) => family.channels.forEach((channel) => {
        const legacyGa4 = channel === ANALYTICS_CHANNEL && connections.useGa4Scripts;
        if (!allowed.has(channel) && !legacyGa4) return;

        const credentials = legacyGa4 ? [] : (connections.credentials[channel] || []);
        const stores = connections.stores[channel] || [];
        const attention = credentials
            .map((credential) => {
                const kind = attentionKindOf(channel, credential, now);
                return kind && {channel, credential, kind, date: attentionDateOf(kind, credential),
                                action: fixedBySendingLink(channel, credential) ? 'send_link' : 'reconnect'};
            })
            .filter(Boolean);
        const platform = {
            channel,
            family: family.key,
            legacyGa4,
            credentials,
            stores,
            connected: legacyGa4 || credentials.length > 0 || stores.length > 0,
            accountsCount: isStoreChannel(channel)
                ? stores.length
                : credentials.reduce((sum, credential) => sum + (Number(credential.ad_accounts_count) || 0), 0),
            unassignedStores: stores.filter((store) => !(store.agency_client_names || []).length).length,
            // `=== false`: un backend que no manda el campo no marca ninguna como caída.
            storesWithoutAccess: stores.filter((store) => store.has_access === false).length,
            attention,
        };
        platform.status = statusOf(platform);
        result.push(platform);
    }));
    return result;
}

/** Las familias con al menos una plataforma conectada, cada una con las suyas. */
export function familiesOf(platforms) {
    return FAMILIES
        .map((family) => ({
            key: family.key,
            platforms: platforms.filter((platform) => platform.family === family.key && platform.connected),
        }))
        .filter((family) => family.platforms.length > 0);
}

/**
 * Los accesos que necesitan atención, para el aviso de arriba: primero los que ya no traen datos
 * (vencidos, desconectados) y después los que están por vencer; dentro de cada grupo, en el orden de
 * las familias. Sin ninguno, el aviso no se dibuja.
 */
export function attentionOf(platforms) {
    const items = [];
    platforms.forEach((platform) => items.push(...platform.attention));
    return items
        .map((item, index) => ({item, index}))
        .sort((a, b) => (ATTENTION_PRIORITY[a.item.kind] - ATTENTION_PRIORITY[b.item.kind]) || (a.index - b.index))
        .map(({item}) => item);
}

/** Las plataformas habilitadas SIN conectar, en el orden del bloque de descubrimiento. */
export function discoveryOf(platforms) {
    const byChannel = {};
    platforms.forEach((platform) => { byChannel[platform.channel] = platform; });
    return DISCOVERY_ORDER
        .map((channel) => byChannel[channel])
        .filter((platform) => platform && !platform.connected);
}

/**
 * Parte el descubrimiento en lo que se ve y lo que el usuario ocultó con la X (D7). Lo oculto cuenta
 * sólo si TODAVÍA se ofrecería: una plataforma oculta que después se conectó ya no es «una que
 * ocultaste».
 */
export function splitDiscovery(discovery, hiddenChannels) {
    const hidden = new Set(hiddenChannels || []);
    return {
        visible: discovery.filter((platform) => !hidden.has(platform.channel)),
        hidden: discovery.filter((platform) => hidden.has(platform.channel)),
    };
}

// --- Lo oculto (D7): por usuario y por agencia, en localStorage ---------------------------------

export function hiddenStorageKey(agencyId, userId) {
    return `dashbo_platforms_hidden_${agencyId}_${userId}`;
}

function defaultStorage() {
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
}

/**
 * Los canales que el usuario ocultó. Sin agencia o sin usuario no hay dónde leer, y un valor roto o
 * un storage bloqueado (ventana privada, sitio sin datos) se leen como «nada oculto».
 */
export function readHiddenChannels(agencyId, userId, storage = defaultStorage()) {
    if (!agencyId || !userId || !storage) return [];
    try {
        const parsed = JSON.parse(storage.getItem(hiddenStorageKey(agencyId, userId)));
        return Array.isArray(parsed) ? parsed.filter((channel) => typeof channel === 'string') : [];
    } catch (error) {
        return [];
    }
}

/** Guarda lo oculto; si no se puede, la pantalla sigue andando y el cambio dura lo que la visita. */
export function writeHiddenChannels(agencyId, userId, channels, storage = defaultStorage()) {
    if (!agencyId || !userId || !storage) return;
    try {
        if (channels.length > 0) {
            storage.setItem(hiddenStorageKey(agencyId, userId), JSON.stringify(channels));
        } else {
            storage.removeItem(hiddenStorageKey(agencyId, userId));
        }
    } catch (error) {
        // Bloqueado: queda oculto mientras dure la visita.
    }
}

// --- Textos: qué clave de `copy.md` §2 le toca a cada canal --------------------------------------

// Las claves de copy nombran Search Console por su nombre, pero el canal se llama `'GSC'`. Mercado Ads lleva
// rótulo y beneficio propios: el rótulo es «Mercado Libre», que dice qué es a quien no lo conoce. Es el subtítulo
// («Publicidad de Mercado Libre») recortado: entero partía la cabecera de la tarjeta (QA-VIS-maf-R01).
const COPY_NAME_BY_CHANNEL = {
    [MERCADO_ADS_CHANNEL]: 'MERCADO_ADS',
    [SEARCH_CONSOLE_CHANNEL]: 'SEARCH_CONSOLE',
    [KLAVIYO_CHANNEL]: 'KLAVIYO',
    [ESCALAFY_CHANNEL]: 'ESCALAFY',
    [ANALYTICS_CHANNEL]: 'ANALYTICS',
};

function copyNameOf(channel) {
    if (COPY_NAME_BY_CHANNEL[channel]) return COPY_NAME_BY_CHANNEL[channel];
    return isStoreChannel(channel) ? 'store' : 'paid_media';
}

/** El rótulo chico de la tarjeta de descubrimiento («SEO», «Tienda», «Paid media»…). */
export function kindKeyOf(channel) {
    return `agency_v2.platforms.kind.${copyNameOf(channel)}`;
}

/** El beneficio de conectarla, en una línea. */
export function benefitKeyOf(channel) {
    return `agency_v2.platforms.benefit.${copyNameOf(channel)}`;
}

/**
 * En qué se cuentan las cuentas de un canal: `'accounts'`, `'properties'` (GA4 y Search Console),
 * `'organizations'` (Escalafy) o `'stores'`. Klaviyo es una cuenta, no una tienda.
 */
export function unitOf(channel) {
    if (channel === ANALYTICS_CHANNEL || channel === SEARCH_CONSOLE_CHANNEL) return 'properties';
    if (channel === ESCALAFY_CHANNEL) return 'organizations';
    if (channel === KLAVIYO_CHANNEL) return 'accounts';
    return isStoreChannel(channel) ? 'stores' : 'accounts';
}

/** «Ver las N cuentas»: la pestaña Cuentas, con el chip de esa plataforma prendido (D1, contrato §3). */
export function accountsPathFor(channel) {
    return `/agency/ad-accounts?plataforma=${encodeURIComponent(channel)}`;
}

const OPEN_ON_ARRIVAL = ['attention', 'expiring', 'store_no_access', 'store_unassigned'];

/**
 * Las plataformas que se abren solas al llegar: las que piden algo (un acceso caído, una tienda sin
 * acceso o sin cliente) y GA4 por planilla, que no tiene resumen ni estado propios: su componente
 * viejo es el que dice si anda, y plegado no se vería (el clásico lo muestra de entrada).
 */
export function initiallyOpenChannels(platforms) {
    return platforms
        .filter((platform) => platform.connected
            && (platform.legacyGa4 || OPEN_ON_ARRIVAL.includes(platform.status.kind)))
        .map((platform) => platform.channel);
}

/**
 * Los canales cuyo acceso despliega sus cuentas y el «¿Falta una cuenta?» (cc#205): los de pauta
 * en los que una cuenta puede no llegar porque el usuario que autorizó no tiene acceso a ella. DV360
 * queda afuera: no hay un texto probado de cómo dar acceso ahí. En Mercado Ads las «cuentas» son los
 * anunciantes del vendedor, uno por país: al volver de conectar se ven desplegados.
 */
export const ACCOUNT_LIST_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL,
    MERCADO_ADS_CHANNEL];

export function hasAccountList(channel) {
    return ACCOUNT_LIST_CHANNELS.includes(channel);
}

/** La clave del «¿Falta una cuenta?» de la plataforma, o `null` si no tiene. */
export function missingAccountKeyOf(channel) {
    return hasAccountList(channel) ? `agency_v2.platforms.missing.${channel}` : null;
}

/** Las cuentas cuyo nombre o ID contiene el texto buscado (sin distinguir mayúsculas). */
export function filterAccessAccounts(accounts, query) {
    const needle = (query || '').trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter((account) => (account.name || '').toLowerCase().includes(needle)
        || String(account.account_id || '').includes(needle));
}

/**
 * Los canales cuya alta puede pedir un modal Vincular cuenta por la URL: los de pauta con lista de
 * cuentas («Conectar … con tu usuario», cc#209) y las herramientas de Google con acceso propio, que el
 * estado vacío manda a conectar («Conectar Search Console», UD-9).
 */
const ADD_FROM_URL_CHANNELS = [...ACCOUNT_LIST_CHANNELS, DV360_CHANNEL, ANALYTICS_CHANNEL, SEARCH_CONSOLE_CHANNEL];

/**
 * El canal cuyo alta hay que abrir al llegar (`?add=<CANAL>`, desde un modal Vincular cuenta), o
 * `null`. Sólo los de `ADD_FROM_URL_CHANNELS`: un valor cualquiera en la URL no abre nada.
 */
export function addChannelFrom(search) {
    const channel = new URLSearchParams(search || '').get('add');
    return ADD_FROM_URL_CHANNELS.includes(channel) ? channel : null;
}

/**
 * El acceso a desplegar al llegar de una conexión (`?just_connected=<CANAL>&credential=<id>`): el que
 * devolvió el backend, que en una reconexión es el que ya existía. `null` si no aplica.
 */
export function credentialToExpandFrom(search) {
    const params = new URLSearchParams(search || '');
    const channel = params.get('just_connected');
    const credentialId = Number(params.get('credential'));
    if (!hasAccountList(channel) || !credentialId) return null;
    return {channel, credentialId};
}
